import { PageInlinks } from "@/components/LaunchBottom";
import { CACHEOR_KEYS } from "@/constants/cacheor";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import Feed from "@/pageComponents/Home/Feed";
import PageTransition from "@/pageComponents/Home/PageTransition";
import {
  batchGetExplores,
  getBoardsListByIds,
  getQingzhouConfig,
  getSeoInlinks,
  getUsersListByIds,
} from "@/services";
import { useHomeUrl } from "@/utils/getHomeUrl";
import { cacheor } from "@/utils/server/cacheor";
import { isLogined } from "@/utils/server/isLogined";
import { serverSideRedirect } from "@/utils/server/serverSide";
import { useAsyncEffect, useMemoizedFn } from "ahooks";
import classNames from "classnames";
import { pick } from "lodash";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import Footer from "../components/Footer";
import styles from "./index.module.less";

const Search = dynamic(() => import("../components/Header/search"), {
  ssr: false,
});

function getUserAndBoardIds(list) {
  let userIds = [];
  let boardIds = [];
  let exploreUrlnames = [];
  list.forEach(({ materials }) => {
    materials.forEach((card) => {
      const material = card?.material;
      if (material?.type === "user" && material?.targetId) {
        userIds.push(material?.targetId);
      } else if (material?.type === "board" && material?.targetId) {
        boardIds.push(material?.targetId);
      } else if (material?.type === "topic" && material?.targetId) {
        exploreUrlnames.push(material?.targetId);
      }
    });
  });

  return { userIds, boardIds, exploreUrlnames };
}

function setBoardAndUser(list, { users, boards, explores }) {
  if (!list.length) return;

  list.forEach(({ materials }) => {
    materials.forEach((card) => {
      const material = card?.material;
      if (material?.type === "user" && users[material.targetId]) {
        material.user = users[material.targetId];
      } else if (material?.type === "board" && boards[material.targetId]) {
        material.board = boards[material.targetId];
      } else if (material?.type === "topic" && explores[material.targetId]) {
        material.explore = explores[material.targetId];
      }
    });
  });
}

/**
 * 清理数据，只保留有用的字段
 * @param {*} param0 需要清理的数据
 * @returns
 */
function cleanData({ userRes, boardRes, exploreRes }) {
  // 只保留有用的字段
  const users = {};
  userRes?.users &&
    userRes.users.forEach(
      (user) =>
        user &&
        (users[user.user_id] = pick(user, [
          "avatar",
          "user_id",
          "username",
          "urlname",
          "pin_count",
          "follower_count",
        ]))
    );

  const boards = {};
  boardRes?.boards &&
    boardRes.boards.forEach(
      (board) =>
        board &&
        (boards[board.board_id] = pick(board, [
          "board_id",
          "title",
          "pin_count",
          "follow_count",
        ]))
    );

  const explores = {};
  exploreRes?.explores &&
    exploreRes.explores.forEach(
      (explore) =>
        (explores[explore.urlname] = pick(explore, ["urlname", "name"]))
    );

  return { users, boards, explores };
}

async function getHomePageQZConf(cacheorKey) {
  const result = await getQingzhouConfig({
    position: "homepage",
  });

  // 获取用户和画板、专题的id集合
  let { userIds, boardIds, exploreUrlnames } = getUserAndBoardIds(result?.pits);

  // 批量获取用户和画板等信息
  const [userRes, boardRes, exploreRes] = await Promise.all([
    userIds.length ? getUsersListByIds(userIds) : null,
    boardIds.length ? getBoardsListByIds(boardIds, 0) : null,
    exploreUrlnames.length
      ? batchGetExplores({ urlnames: exploreUrlnames })
      : null,
  ]);

  setBoardAndUser(result?.pits, cleanData({ userRes, boardRes, exploreRes }));

  // 缓存数据
  cacheor.set({ key: cacheorKey, value: result, ttl: 60 * 60 });

  return result;
}

export async function getServerSideProps({ req, res, query }) {
  // 获取页面配置
  const pageConf = await cacheor.getGrocery({
    path: "home-page-setting",
    disableCache: query?._cache,
  });

  // 避免客户端页面多次跳转
  if (isLogined(req) && !req.url.includes(".json")) {
    // 实验结果直接返回发现页
    return serverSideRedirect(res, {
      destination: encodeURI(`/discovery`),
      permanent: false,
    });
  }

  const allRequest = [
    getSeoInlinks({
      title:
        "花瓣网 - 陪你做生活的设计师（创意图片大全、设计灵感图库、高清图片素材）",
      current_url: "home",
    }),
  ];

  const cacheorKey = CACHEOR_KEYS.HOME_PAGE;
  async function getCacheData() {
    const cacheData = await cacheor.get({ key: cacheorKey });

    if (!cacheData) {
      return getHomePageQZConf(cacheorKey);
    }

    return cacheData;
  }

  if (!query?._cache) {
    allRequest.push(getCacheData());
  } else {
    allRequest.push(getHomePageQZConf(cacheorKey));
  }

  const [inlinks, homeConf] = await Promise.all(allRequest);

  // 将第一个放到最后
  const firstPit = homeConf?.pits[0];
  homeConf.pits = homeConf?.pits.slice(1);
  homeConf.pits.push(firstPit);

  return {
    props: {
      pageConf,
      /** */
      homeConf,
      inlinks,
    },
  };
}

const Home = ({ homeConf, inlinks }) => {
  const router = useRouter();
  const { user, userReady } = useAppStore(({ user, userReady }) => ({
    user,
    userReady,
  }));

  const targetUrl = useHomeUrl(user);

  useAsyncEffect(async () => {
    if (userReady && user) {
      router.push(targetUrl);
    }
  }, [userReady, user, router, targetUrl]);

  // 当前页面
  const [pageStep, setPageStep] = useState(0);

  const maxStep = homeConf.pits.length;

  const goStep = useMemoizedFn((index) => {
    setPageStep(index);
  });

  const nextStep = useMemoizedFn(() => {
    setPageStep(Math.min(pageStep + 1, maxStep - 1));
  });

  const prevStep = useMemoizedFn(() => {
    setPageStep(Math.max(pageStep - 1, 0));
  });

  useEffect(() => {
    if (pageStep < maxStep - 1) {
      window.dispatchEvent(
        new CustomEvent(EVENT.CHANGE_SEARCH_INPUT_VISIBLE, {
          detail: true,
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent(EVENT.CHANGE_SEARCH_INPUT_VISIBLE, {
          detail: false,
        })
      );
    }
  }, [maxStep, pageStep]);

  const { current } = useRef({
    isScroll: true, // 控制是否可以滚动
    scrollTime: null, // 上次触发的时间戳（ms）
    timer: null, // setTimeout句柄
    interval: 500, // 两次滑动之间的最小事件间隔
    distance: 60, // 控制有效滑动的最小距离
    delay: 200, // 防抖时间
  });

  const wheel = useMemoizedFn((e) => {
    if (e.deltaY > 0) {
      nextStep();
    } else {
      prevStep();
    }
  });

  const onWheel = useMemoizedFn((e) => {
    const time = new Date().getTime();
    const y = e.deltaY; // 以垂直滚动为列

    if (current.scrollTime) {
      // 触发时间间隔在设置值内 且 滚动距离超过设置最小间隔
      if (
        time - current.scrollTime > current.interval &&
        Math.abs(y) >= current.distance
      ) {
        // 触摸板滚动
        wheel(e);
        clearTimeout(current.timer);
        current.isScroll = false;
        current.scrollTime = time;
      }
    }
    if (current.isScroll) {
      // 正常滚动
      wheel(e);
      current.scrollTime = new Date().getTime();
      current.isScroll = false;
    }
    if (current.timer) {
      clearTimeout(current.timer);
    }
    current.timer = setTimeout(() => {
      current.isScroll = true;
    }, current.delay);
  });

  // 滚动切屏
  useEffect(() => {
    window.addEventListener("wheel", onWheel);
    return () => {
      window.removeEventListener("wheel", onWheel);
    };
  }, [nextStep, prevStep, onWheel]);

  return (
    <div className={styles.container}>
      {/* 首页不需要滚动条 */}
      <style>{` body { overflow: hidden; } `}</style>

      {/* 标语部分 */}
      <div className={styles.head}>
        <PageTransition
          className={styles.titleContent}
          show={pageStep <= homeConf.pits.length - 2}
          direction={"up"}
          delay={pageStep <= homeConf.pits.length - 2 ? 100 : 0}
        >
          <div className={styles.title}>
            专业设计师在<span>花瓣</span>发现、收集灵感
          </div>

          <div className={styles.tab}>
            {homeConf.pits.map(
              (pit, index) =>
                index < maxStep - 1 && (
                  <div
                    className={classNames(styles.item, {
                      [styles.active]: pageStep === index,
                    })}
                    onClick={() => goStep(index)}
                    key={index}
                  >
                    {pit.name}
                  </div>
                )
            )}
          </div>
        </PageTransition>

        <PageTransition
          show={pageStep === maxStep - 1}
          direction={"down"}
          delay={pageStep === maxStep - 1 ? 100 : 0}
          className={styles.titleContent}
        >
          <h1 className={styles.title}>
            <span>花瓣，</span>陪你做生活的设计师
          </h1>
          <Search
            width={570}
            placeholderCenter={true}
            hideQuickSearch={true}
            allowClear={false}
            className={styles.search}
          ></Search>
        </PageTransition>
      </div>

      <div className={styles.feed}>
        {homeConf.pits.map((pit, index) => (
          <PageTransition
            key={index}
            className={styles.pageTransition}
            show={pageStep === index}
            direction={pageStep >= index ? "up" : "down"}
            delay={pageStep === index ? 100 : 0}
          >
            <Feed
              list={pit?.materials || []}
              name={pit.name}
              lazyImg={pageStep !== index}
            />
          </PageTransition>
        ))}
      </div>

      <div className={styles.nav}>
        {homeConf.pits.map((item, index) => {
          return (
            <div
              key={index}
              className={classNames(styles.item, {
                [styles.active]: pageStep === index,
              })}
              onClick={() => goStep(index)}
            ></div>
          );
        })}
      </div>
      <div
        className={classNames(styles.inlinks, {
          [styles.hide]: maxStep - 1 !== pageStep,
        })}
      >
        <PageInlinks inlinks={inlinks} />
      </div>
      <Footer style={{ zIndex: 11 }} />
    </div>
  );
};

export default Home;
